import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { ActionMeta, OnChangeValue } from 'react-select';
import { ActorRefFrom } from 'xstate';
import {
  AsyncSelect,
  FormRow,
  FormSection,
  Multiselect,
  MULTISELECT_STYLES,
  Select,
  TextInput,
} from '~/components';
import { ButtonFooter } from '~/components/buttons/ButtonFooter';
import { loadCategoryOptions, loadTypeOptions } from './api';
import { LANGUAGES } from './constants';
import type { profileFormMachine } from './profileForm.machine';
import {
  OptionNumType,
  OptionStringType,
  ProfessionalInfoSchema,
  ProfessionalInfoValues,
} from './types';
import { useProfileDraftForm, type ProfileDrafts } from './useProfileDraftForm';
import { normalizeNpiInput, orderOptions } from './utils';

const TAGS = CT.profile.professionalInfo.form;

interface ProfessionalInfoProps {
  actor: ActorRefFrom<typeof profileFormMachine>;
  drafts?: ProfileDrafts;
  values: Partial<ProfessionalInfoValues>;
}

export const ProfessionalInformationForm = ({ actor, drafts }: ProfessionalInfoProps) => {
  const [typeOptions, setTypeOptions] = useState<OptionNumType[]>([]);
  const professionalInfo = useSelector(actor, (state) => state.context.professionalInfo);
  const methods = useProfileDraftForm<ProfessionalInfoValues>(
    {
      resolver: zodResolver(ProfessionalInfoSchema),
      values: professionalInfo,
    },
    drafts?.professional
  );

  const { handleSubmit, watch, setValue } = methods;

  const providerCategory = watch('providerCategory');
  const providerCategoryId = providerCategory?.value;

  const langs = orderOptions(watch('languages') ?? []);

  useEffect(() => {
    let current = true;
    setTypeOptions([]);
    if (providerCategoryId === undefined) return;
    loadTypeOptions(providerCategoryId).then((options) => {
      if (current) setTypeOptions(options);
    });
    return () => {
      current = false;
    };
  }, [providerCategoryId]);

  const onSubmitSuccess = async (values: ProfessionalInfoValues) =>
    actor.send({ type: 'ON_SUBMIT_PROFESSIONAL', values });
  const onSubmitError = (err: unknown) =>
    console.debug('ProfessionalInfo form validation error:', err);

  const onLanguageChange = (
    newValue: OnChangeValue<OptionStringType, true>,
    actionMeta: ActionMeta<OptionStringType>
  ) => {
    switch (actionMeta.action) {
      case 'pop-value':
        if (actionMeta.removedValue.isFixed) {
          newValue = langs;
        }
        break;
      case 'clear':
        newValue = LANGUAGES.filter((l) => l.isFixed);
        break;
      default:
        break;
    }
    setValue('languages', [...newValue], { shouldDirty: true });
  };

  return (
    <FormProvider {...methods}>
      <form className="flex flex-col gap-5" onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormSection componentsPerLine={2}>
          <FormRow>
            <TextInput
              name={'npiNum'}
              cyTag={TAGS.NpiNumberInput}
              label={'NPI Number'}
              placeholder={''}
              inputMode="numeric"
              maxLength={10}
              onChange={(event) => {
                event.target.value = normalizeNpiInput(event.target.value);
              }}
            />
          </FormRow>
          <FormRow>
            <AsyncSelect
              wrapValueOnMobile
              label={'Provider Category'}
              name={'providerCategory'}
              onChange={() => setValue('providerType', null)}
              loadOptions={loadCategoryOptions}
              cyTag={TAGS.providerCatSelect}
              minChars={false}
            />
          </FormRow>
          <FormRow>
            <Select
              wrapValueOnMobile
              name={'providerType'}
              label={'Provider Type'}
              cyTag={TAGS.providerTypeSelect}
              options={typeOptions}
              disabled={!providerCategory}
            />
          </FormRow>
          <FormRow>
            <Multiselect<ProfessionalInfoValues>
              name={'languages'}
              value={langs}
              label={'Languages'}
              styles={MULTISELECT_STYLES}
              isClearable={LANGUAGES.some((l) => !l.isFixed)}
              options={LANGUAGES}
              onChange={onLanguageChange}
              cyTag={TAGS.languagesMultiSelect}
            />
          </FormRow>
        </FormSection>
        <ButtonFooter primaryButton={{ label: 'Save', cyTag: TAGS.submitBtn, isSubmit: true }} />
      </form>
    </FormProvider>
  );
};
